// src/components/contact-usPage.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Fade, Slide, Zoom } from 'react-awesome-reveal';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from 'react-icons/fa'; // Icons for contact info

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const navigate = useNavigate();
  const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const RECAPTCHA_SITE_KEY = isLocalhost
  ? null
  : (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcvAwEtAAAAAOzRSm46-IXbt_xxAlStAD5zYorx');

  const mailEndpoint = process.env.NODE_ENV === 'development' ? '/api/sendmail' : '/sendmail.php';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = (e) => {
    e.preventDefault();
    setFormData({ name: '', email: '', subject: '', phone: '', message: '' });
    setErrorMessage('');
    setSuccessMessage('');
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      setIsRecaptchaReady(true);
      return;
    }

    const scriptId = 'recaptcha-v3-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => setIsRecaptchaReady(true));
        }
      };
      document.body.appendChild(script);
    } else if (window.grecaptcha) {
      window.grecaptcha.ready(() => setIsRecaptchaReady(true));
    }
  }, [RECAPTCHA_SITE_KEY]);

  const executeRecaptcha = async (action) => {
    if (!RECAPTCHA_SITE_KEY) {
      return 'dev-localhost-bypass';
    }
    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA is not loaded.');
    }
    return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  };

  const sendContactForm = async (captchaToken) => {
    try {
      const response = await fetch(mailEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          phone: formData.phone,
          message: formData.message,
          captchaToken: captchaToken
        })
      });
      
      const responseText = await response.text();
      if (!response.ok) {
        console.error('Non-OK response body:', responseText);
        throw new Error(`HTTP error! status: ${response.status} body: ${responseText}`);
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (err) {
        console.error('Invalid JSON response from', mailEndpoint, responseText);
        throw new Error('Invalid JSON response: ' + responseText);
      }
      if (result.success) {
        setSuccessMessage('Message sent successfully!');
        setFormData({ name: '', email: '', subject: '', phone: '', message: '' });
        setTimeout(() => navigate('/thank-you'), 1500);
      } else {
        setErrorMessage('Error sending message: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Form error:', error);
      setErrorMessage('Error sending message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    if (!isRecaptchaReady) {
      setErrorMessage('reCAPTCHA is not ready yet. Please wait a moment and try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      const captchaToken = await executeRecaptcha('contact_form');
      await sendContactForm(captchaToken);
    } catch (error) {
      console.error('Submit error:', error);
      setErrorMessage('Error sending message. Please try again.');
      setIsSubmitting(false);
    }
  };
  return (
    <div>
      {/* ==================== 1. Page Header (Breadcrumb) ==================== */}
      <section className="py-5 text-center d-flex align-items-center" style={{
        minHeight: '40vh',
        background: 'linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.8)), url("/assets/banner/breadcrumb.jpg") no-repeat center center',
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed'
      }}>
        <Container className="position-relative" style={{ zIndex: 2 }}>
          <Fade triggerOnce direction="down">
            <h1 className="display-4 fw-bold mb-3 text-white">Contact Us</h1>
            <p className="lead text-white-50">Home / Contact Us</p>
          </Fade>
        </Container>
      </section>

      {/* ==================== 2. Contact Information ==================== */}
      <section className="bg-light-amiso">
        <Container>
          <Row className="justify-content-center g-4">
            <Col lg={4} md={6}>
              <Zoom triggerOnce delay={100}>
                <Card className="amiso-card h-100 p-3 text-center">
                  <Card.Body>
                    <FaMapMarkerAlt size={45} className="mb-3" />
                    <h5 className="fw-bold text-white mb-2">Address</h5>
                    <p className="text-muted mb-0">Andheri East, Mumbai, Maharashtra</p>
                  </Card.Body>
                </Card>
              </Zoom>
            </Col>
            <Col lg={4} md={6}>
              <Zoom triggerOnce delay={200}>
                <Card className="amiso-card h-100 p-3 text-center">
                  <Card.Body>
                    <FaPhoneAlt size={45} className="mb-3" />
                    <h5 className="fw-bold text-white mb-2">Phone</h5>
                    <p className="text-muted mb-0">+91 6388414242</p>
                  </Card.Body>
                </Card>
              </Zoom>
            </Col>
            <Col lg={4} md={6}>
              <Zoom triggerOnce delay={300}>
                <Card className="amiso-card h-100 p-3 text-center">
                  <Card.Body>
                    <FaEnvelope size={45} className="mb-3" />
                    <h5 className="fw-bold text-white mb-2">Email</h5>
                    <p className="text-muted mb-0">vishal@webbloom.in</p>
                  </Card.Body>
                </Card>
              </Zoom>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== 3. Contact Form ==================== */}
      <section className="bg-dark">
        <Container>
          <Fade triggerOnce direction="down">
            <span className="text-divider d-block text-center mb-3">Get in Touch</span>
            <h2 className="section-title text-center text-white mb-5">
             Let’s Connect & Build <br /> Something Great
            </h2>
          </Fade>

          <Row className="justify-content-center">
            <Col lg={8}>
              <Slide triggerOnce direction="up">
                <Form className="amiso-card p-5" onSubmit={handleSubmit}>
                  <Row className="mb-4">
                    <Col md={6} className="mb-3">
                      <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your Name" className=" border-0 py-3" required />
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Your Email" className="border-0 py-3" required />
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col md={6} className="mb-3">
                      <Form.Control type="text" name="subject" value={formData.subject} onChange={handleChange} placeholder="Subject" className="border-0 py-3" required />
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Control type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" className="border-0 py-3" required/>
                    </Col>
                  </Row>
                  <Form.Group className="mb-4">
                    <Form.Control as="textarea" name="message" value={formData.message} onChange={handleChange} rows={7} placeholder="Your Message" className=" border-0 py-3" />
                  </Form.Group>
                  {errorMessage && (
                    <div className="alert alert-danger mb-4" role="alert">
                      {errorMessage}
                    </div>
                  )}
                  {successMessage && (
                    <div className="alert alert-success mb-4" role="alert">
                      {successMessage}
                    </div>
                  )}
                  <div className="d-grid">
                    <Button variant="primary" type="submit" size="lg" className="px-5 py-3" disabled={isSubmitting}>{isSubmitting ? 'Submitting...' : 'Submit'}</Button>
                  </div>
                </Form>
              </Slide>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== 4. Google Map Placeholder ==================== */}
      <section className="p-0">
        <div style={{ width: '100%', height: '450px', overflow: 'hidden' }}>
          {/* Replace with actual Google Maps Embed code */}
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d27292.053102382448!2d72.84789083500013!3d19.11478125400465!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b61b41dfb131%3A0xa668297563ddac31!2sAndheri%20East%2C%20Mumbai%2C%20Maharashtra!5e1!3m2!1sen!2sin!4v1778012811384!5m2!1sen!2sin" 
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Our Location"
          ></iframe>  
        </div>
      </section>
    </div>
  );
}

export default ContactPage;